import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check, Eye, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { IconButton } from '../common/icon-button';
import { PreviewVideoImageFull } from '../common/preview-video-image-full';
import { FallbackImage } from '../common/fallback-image';
import { buildImageOssResize } from '@/utils/build-image-oss-resize ';

type TemplateItem = {
  id: number;
  coverUrl: string;
  name: string;
};

interface TemplateChooseModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  templates: TemplateItem[];
  selectedId?: number;
  onOpenChange?: (open: boolean) => void;
  onSave?: (template: TemplateItem) => void;
}

/** 模板选择弹窗（短视频/图文/文章） */
export const TemplateChooseModal: React.FC<TemplateChooseModalProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  templates,
  selectedId: externalSelectedId,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const [selectedId, setSelectedId] = useState<number | null>(externalSelectedId ?? null);
  const [showPreview, setShowPreview] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;
  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  useEffect(() => {
    setSelectedId(externalSelectedId ?? null);
  }, [externalSelectedId]);


  // 处理样式选择
  const handleSelected = (id: number) => {
    setSelectedId(id);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    onSave?.(templates.find((item) => item.id === selectedId));
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  // 处理滚动逻辑
  useEffect(() => {
    if (currentOpen && selectedId) {
      // 使用 setTimeout 确保 DOM 已经完全渲染并计算了位置
      const timer = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',  // 滚动到容器中心位置
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentOpen, selectedId]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[750px] h-[755px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t('agent_creation.template_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none mb-5 overflow-hidden'>
          <div className='w-full h-full relative overflow-hidden'>
            <div
              className='pt-0.5 pl-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
              <div className='grid grid-cols-6 gap-x-3 gap-y-5'>
                {templates?.map((item, index) => {
                  const isSelected = selectedId === item.id;
                  return (
                    <div
                      key={`${item.id}-${index}`}
                      className="w-full h-full"
                      {...(isSelected ? { ref: selectedItemRef } : {})}
                    >
                      <div
                        className={cn(
                          "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                          selectedId === item.id
                            ? "outline outline-2 outline-i-normal"
                            : "outline outline-transparent"
                        )}
                        onClick={() => handleSelected(item.id)}
                      >
                        <div className='size-full rounded-[12px] flex-shrink-0'>
                          <FallbackImage
                            primarySrc={buildImageOssResize(item.coverUrl, { w: 102 * 3, h: 136 * 3 })}
                            fallbackSrc={item.coverUrl}
                            className="size-full rounded-[12px] object-cover border border-[#000000]/10"
                          />
                          <IconButton
                            icon={<Eye className="size-5 text-white " />}
                            className='absolute left-2 bottom-1.5 z-50 transition-all opacity-0 group-hover:opacity-100 bg-black/40 hover:bg-black/60 '
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPreview(true);
                              setCurrentIndex(index);
                            }}
                          />
                        </div>
                        {/* 选中样式 */}
                        <div
                          className={cn(
                            "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                            selectedId === item.id && "opacity-100"
                          )}
                        >
                        </div>
                        <Check className={cn(
                          "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                          selectedId === item.id && "opacity-100"
                        )} />
                      </div>
                      <div className="w-full text-center mt-3">
                        <div className="w-full">
                          <TruncatedText text={item.name} className='max-w-[90%] text-sm text-[#333333]' />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 z-20'>
          <TextButton
            label={t('agent_creation.template_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_creation.template_modal.confirm')}
            className='w-[135px] h-11'
            onClick={handleSave}
          />
        </div>
        {/* 预览弹窗 */}
        {showPreview && (
          <PreviewVideoImageFull
            open={showPreview}
            previewList={templates.map((item) => ({
              url: item.coverUrl,
              type: "image",
            }))}
            currentIndex={currentIndex}
            onOpenChange={setShowPreview}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
